import { useState, useEffect } from 'react';

export interface ExpenseData {
    date_of_service: string;
    provider: string;
    patient_name: string;
    amount: number;
    category: string;
    family_member: string;
}

interface Props {
    expense?: Partial<ExpenseData>;
    onSave: (data: ExpenseData) => void;
    onCancel?: () => void;
    saving?: boolean;
    onChange?: (data: ExpenseData) => void;
    descriptionLabel?: string; // Label for the patient_name field
}

const CATEGORIES = [
    { value: '', label: 'Select category' },
    { value: 'medical', label: 'Medical' },
    { value: 'dental', label: 'Dental' },
    { value: 'vision', label: 'Vision' },
    { value: 'prescriptions', label: 'Prescriptions' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'otc', label: 'Over-the-Counter' },
];

const FAMILY_MEMBERS = [
    { value: '', label: 'Select family member' },
    { value: 'self', label: 'Self' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'dependent', label: 'Dependent' },
];

export default function ExpenseForm({ expense, onSave, onCancel, saving, onChange, descriptionLabel = 'Patient Name' }: Props) {
    const [formData, setFormData] = useState<ExpenseData>({
        date_of_service: expense?.date_of_service || new Date().toISOString().split('T')[0],
        provider: expense?.provider || '',
        patient_name: expense?.patient_name || '',
        amount: expense?.amount || 0,
        category: expense?.category || '',
        family_member: expense?.family_member || '',
    });

    useEffect(() => {
        if (expense) {
            setFormData({
                date_of_service: expense.date_of_service || formData.date_of_service,
                provider: expense.provider ?? formData.provider,
                patient_name: expense.patient_name ?? formData.patient_name,
                amount: expense.amount ?? formData.amount,
                category: expense.category ?? formData.category,
                family_member: expense.family_member ?? formData.family_member,
            });
        }
    }, [expense]);

    function handleChange(field: keyof ExpenseData, value: string | number) {
        const updated = { ...formData, [field]: value };
        setFormData(updated);
        onChange?.(updated);
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        onSave(formData);
    }

    return (
        <form onSubmit={handleSubmit} className="expense-form">
            <div className="form-group">
                <label htmlFor="date_of_service">Date of Service *</label>
                <input
                    id="date_of_service"
                    type="date"
                    value={formData.date_of_service}
                    onChange={(e) => handleChange('date_of_service', e.target.value)}
                    required
                />
                <small>IRS requires the date service was provided, not payment date</small>
            </div>

            <div className="form-group">
                <label htmlFor="provider">Provider/Merchant *</label>
                <input
                    id="provider"
                    type="text"
                    value={formData.provider}
                    onChange={(e) => handleChange('provider', e.target.value)}
                    placeholder="e.g., Walgreens, Dr. Smith"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="amount">Amount *</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                        required
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="patient_name">{descriptionLabel}</label>
                <input
                    id="patient_name"
                    type="text"
                    value={formData.patient_name}
                    onChange={(e) => handleChange('patient_name', e.target.value)}
                    placeholder="Who received the service"
                />
            </div>

            <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                >
                    {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label htmlFor="family_member">Family Member</label>
                <select
                    id="family_member"
                    value={formData.family_member}
                    onChange={(e) => handleChange('family_member', e.target.value)}
                >
                    {FAMILY_MEMBERS.map((member) => (
                        <option key={member.value} value={member.value}>{member.label}</option>
                    ))}
                </select>
            </div>

            <div className="form-actions">
                {onCancel && (
                    <button type="button" onClick={onCancel} className="btn btn-secondary">
                        Cancel
                    </button>
                )}
                <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Expense'}
                </button>
            </div>
        </form>
    );
}
